import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  cellDobleRowOperation,
  cellNumOperation,
  cellOperation,
  headerOperation,
  headerDoubleRowOperation,
} from '../../../../helpers/tables';
import FilterSelect from '../../../shared/FilterSelect';
import { tiposDeudorToFilterOptions } from '../../../../helpers/selects';
import ClientSideTable from '../../../shared/ClientSideTable';

const CreditLineTable = (props) => {
  const { lineas, tiposDeudor, handleEditLinea, handleDeleteLinea } = props;
  const tiposDeudorOptions = tiposDeudorToFilterOptions(tiposDeudor);

  // Check if all records have address, email, and phone
  const allRecordsHaveContactInfo = lineas.every(linea =>
    linea.deudor.address && linea.deudor.email && linea.deudor.phone
  );

  const columns = [
    {
      Header: headerOperation('Deudor'),
      id: 'deudor',
      accessor: 'deudor',
      Cell: (row) => cellDobleRowOperation(row.value.rut, row.value.name),
      filterMethod: (filter, row) => {
        const { deudor } = row;
        return deudor !== undefined
          ? deudor.rut.includes(filter.value) || deudor.name.includes(filter.value)
          : true;
      },
      sortMethod: (a, b) => {
        return a.name.localeCompare(b.name);
      },
      minWidth: 150,
    },
    {
      Header: headerOperation('Dirección'),
      id: 'address',
      accessor: 'deudor',
      Cell: (row) => cellOperation(row.value.address, 'left'),
      filterable: false,
      width: allRecordsHaveContactInfo ? 100 : 100,
    },
    {
      Header: headerOperation('Email'),
      id: 'email',
      accessor: 'deudor',
      Cell: (row) => cellOperation(row.value.email),
      filterable: false,
      width: allRecordsHaveContactInfo ? 100 : 100,
    },
    {
      Header: headerOperation('Teléfono'),
      id: 'phone',
      accessor: 'deudor',
      Cell: (row) => cellOperation(row.value.phone),
      filterable: false,
      width: allRecordsHaveContactInfo ? 100 : 100,
    },
    {
      Header: headerOperation('Tipo'),
      id: 'tipo_deudor',
      accessor: 'tipo_deudor.descripcion',
      Cell: (row) => cellOperation(row.value),
      Filter: (obj) => (
        <FilterSelect
          filter={obj.filter}
          defaultOption={{ label: 'Todos', value: 'Todos' }}
          onFilterChange={obj.onChange}
          options={tiposDeudorOptions}
        />
      ),
      filterMethod: (filter, row) => {
        const { tipo_deudor: tipoDeudor } = row;
        return tipoDeudor !== undefined && filter.value !== 'Todos'
          ? tipoDeudor === filter.value
          : true;
      },
      minWidth: 110,
    },
    {
      Header: headerDoubleRowOperation('Línea', 'Vigente'),
      id: 'monto_vigente',
      accessor: (i) => cellNumOperation(i.balance.monto_vigente),
      filterable: false,
      width: 90,
    },
    {
      Header: headerDoubleRowOperation('Línea', 'Solicitada'),
      id: 'monto_solicitado',
      accessor: (i) => cellNumOperation(i.balance.monto_solicitado),
      filterable: false,
      width: 90,
    },
    {
      Header: headerOperation('Observación'),
      id: 'observacion',
      accessor: 'balance.observacion',
      Cell: (row) => cellOperation(row.value),
      filterable: false,
      width: 200,
    },
    {
      Header: headerDoubleRowOperation('Carpeta', 'Tributaria'),
      id: 'carpeta_tributaria',
      accessor: (i) => cellOperation(i.balance.carpeta_tributaria ? 1 : 0, 'right'),
      filterable: false,
      width: 120,
    },
    {
      Header: headerDoubleRowOperation('Otros', 'Documentos'),
      id: 'documentos',
      accessor: (i) => cellOperation(i.balance.documentos.length, 'right'),
      filterable: false,
      width: 120,
    },
    {
      Header: headerOperation('Acciones'),
      id: 'actions',
      accessor: (i) => (
        <div className="table-cell" style={{ textAlign: 'center' }}>
          <button
            type="button"
            className="table-link"
            onClick={() => handleEditLinea(i)}
            title="editar"
            disabled={!!i.id}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          &nbsp;&nbsp;
          <button
            type="button"
            className="table-link"
            onClick={() => handleDeleteLinea(i)}
            title="eliminar"
            disabled={!!i.id}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        </div>
      ),
      filterable: false,
      sortable: false,
      width: 100,
    },
  ];

  return <ClientSideTable columns={columns} data={lineas} style={{ maxHeight: '63vh' }} />;
};

CreditLineTable.propTypes = {
  lineas: PropTypes.arrayOf(
    PropTypes.shape({
      cliente: PropTypes.shape({
        rut: PropTypes.string,
        nombre: PropTypes.string,
      }),
      deudor: PropTypes.shape({
        rut: PropTypes.string,
        nombre: PropTypes.string,
        address: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      }),
      tipo_deudor: PropTypes.shape({
        id: PropTypes.number,
        nemotecnico: PropTypes.string,
        descripcion: PropTypes.string,
      }),
      balance: PropTypes.shape({
        monto_vigente: PropTypes.number,
        monto_solicitado: PropTypes.number,
      }),
    }),
  ).isRequired,
  tiposDeudor: PropTypes.arrayOf(
    PropTypes.shape({
      descripcion: PropTypes.string,
    }),
  ).isRequired,
  handleEditLinea: PropTypes.func.isRequired,
  handleDeleteLinea: PropTypes.func.isRequired,
};

export default CreditLineTable;
